/**
 * LinkOwnersModal.tsx — Forecast programme F4b (register row 81): the modal
 * behind "Link owners ›" on the Attribution card (spec §3.4), rendered from
 * forecastLinkOwners.ts's model — the rules are stated there.
 *
 * One row per unattributed won deal: its value, the owner text it carries,
 * an employee picker and two actions.
 *   - "Link owner" — ONE PUT /v1/sales/deals/{id} with `assignedEmployeeId`
 *     (the existing single-deal write; N deals are N requests, never a bulk).
 *   - "Keep unlinked" — the same PUT with `keepOwnerUnlinked`: the deal stays
 *     listed, marked, and the server stops proposing an owner for it.
 * The picker is pre-selected ONLY by a proposal the server sent, and only
 * while that employee is in the directory the picker lists.
 * Authority is the server's money gate; a refusal is surfaced verbatim
 * through apiErrorMessage. After every successful write the tab refetches
 * the period silently (F3's mechanism), so the card, the hero and the By rep
 * grid recalculate and this list re-reads from the new response.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import { X } from 'lucide-react'
import { apiErrorMessage } from '../../../../api/client'
import type { SalesForecast } from '../../../../api/types'
import { overlay } from '../../../../theme/tokens'
import { LoadingState } from '../../DashboardShared'
import { useAsync, fetchDirectory, updateDeal } from '../../api'
import { linkOwnersModel, type LinkOwnerRow } from './forecastLinkOwners'
import { periodName } from './forecastHero'

export function LinkOwnersModal({ data: f, onClose, onChanged }: { data: SalesForecast; onClose: () => void; onChanged?: () => void }) {
  const m = linkOwnersModel(f)
  // ONE directory read for every row's picker (an OwnerSelect per row would
  // make one read per deal). Leavers are not offered, as in OwnerSelect.
  const directory = useAsync(() => fetchDirectory(), [])
  const people = (directory.data ?? []).filter(e => e.status !== 'leaver')
  const [picked, setPicked] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<string | null>(null) // dealId mid-write

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  /** The row's selection: the user's pick, else the server's proposal while
   *  that employee is in the list, else nothing. */
  const selectionOf = (r: LinkOwnerRow): string => {
    if (r.dealId in picked) return picked[r.dealId]
    return r.proposal && people.some(p => p.id === r.proposal!.employeeId) ? r.proposal.employeeId : ''
  }

  async function link(r: LinkOwnerRow) {
    const employeeId = selectionOf(r)
    if (!employeeId || busy) return
    setBusy(r.dealId)
    try {
      await updateDeal(r.dealId, { assignedEmployeeId: employeeId })
      toast.success(`Owner linked on ${r.title}`)
      onChanged?.()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to link the owner'))
    } finally {
      setBusy(null)
    }
  }
  async function keep(r: LinkOwnerRow) {
    if (busy) return
    setBusy(r.dealId)
    try {
      await updateDeal(r.dealId, { keepOwnerUnlinked: true })
      toast.success(`${r.title} is kept unlinked`)
      onChanged?.()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to keep the deal unlinked'))
    } finally {
      setBusy(null)
    }
  }

  const title = `Link owners for ${periodName(f.periodKind, f.periodStart)}`
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div role="dialog" aria-modal="true" aria-label={title} className="k-card"
        style={{ width: 'min(760px, calc(100vw - 32px))', maxHeight: 'calc(100vh - 64px)', overflowY: 'auto' }}>
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-4 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">{title}</div>
            {m && <>
              <div className="text-12 text-ink-muted mt-1" data-link-owners-summary>{m.summary}</div>
              <div className="text-12 text-ink-muted mt-1" data-link-owners-matching>{m.matching}</div>
            </>}
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5">
          {!m && <p className="text-13 text-ink-body" data-link-owners-empty>Every won deal in this period has an owner link.</p>}
          {m && directory.loading && <LoadingState rows={2} />}
          {m && !directory.loading && (
            <>
              {directory.error && (
                <p className="text-12 text-ink-muted mb-3">The employee list could not be loaded, so no owner can be picked. A deal can still be kept unlinked.</p>
              )}
              <ul className="flex flex-col gap-3" aria-label="Won deals with no owner link">
                {m.rows.map(r => {
                  const selection = selectionOf(r)
                  const rowBusy = busy === r.dealId
                  return (
                    <li key={r.dealId} className="p-3 bg-surface-panel flex flex-col gap-2" style={{ borderRadius: 12 }}
                      data-deal-id={r.dealId} data-kept={r.kept} data-proposed={r.proposal?.employeeId ?? ''}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-13 font-semibold text-ink-primary">{r.title}</div>
                          <div className="text-12 text-ink-muted" data-owner-line>{r.ownerLine}</div>
                        </div>
                        <div className="text-13 text-ink-body whitespace-nowrap">{r.valueText}</div>
                      </div>
                      {r.keptLine && <div className="text-12 text-ink-body" data-kept-line>{r.keptLine}</div>}
                      {r.proposalLine && <div className="text-12 text-ink-body" data-proposal-line>{r.proposalLine}</div>}
                      <div className="flex items-center gap-2 flex-wrap">
                        <select value={selection} disabled={rowBusy || !!directory.error}
                          onChange={e => setPicked(p => ({ ...p, [r.dealId]: e.target.value }))}
                          aria-label={`Owner for ${r.title}`}
                          className="px-2 py-1 border border-edge-control rounded-control text-13 text-ink-primary bg-surface-card">
                          <option value="">Choose an employee…</option>
                          {people.map(p => <option key={p.id} value={p.id}>{p.fullName}</option>)}
                        </select>
                        <button type="button" className="k-btn" disabled={!selection || busy !== null}
                          aria-label={`Link owner for ${r.title}`} onClick={() => void link(r)}>
                          {rowBusy ? 'Saving…' : 'Link owner'}
                        </button>
                        {!r.kept && (
                          <button type="button" className="k-btn-secondary" disabled={busy !== null}
                            aria-label={`Keep ${r.title} unlinked`} onClick={() => void keep(r)}>
                            Keep unlinked
                          </button>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
